/**
 * ChatSidecar — always-visible right pane (~380px) inside the BriefWorkspace.
 *
 * Collapses to a 48px quick-action strip when the user clicks the collapse
 * button.  The sidecar (and the chat thread) remains mounted across lens
 * switches — messages persist.
 */

import { ChevronLeft, ChevronRight, GitBranch, Lightbulb, Loader2, MessageSquare, Sparkles, Target, X, Zap } from 'lucide-react';
import { BriefChatPanel } from '@neurograph/brief-ui';
import { ContextualSuggestionChips } from './ContextualSuggestionChips';
import { WORKSPACE_LAYOUT, type LensKey } from './constants';
import type { UseBriefWorkspaceChatReturn } from './useBriefWorkspaceChat';
import type { GenerationBrief } from '@/services/api';

interface QuickAction {
  icon: typeof MessageSquare;
  label: string;
  prompt: string;
  color: string;
}

const QUICK_ACTIONS: QuickAction[] = [
  {
    icon: MessageSquare,
    label: 'Open chat',
    prompt: '',
    color: 'text-brand-purple hover:bg-brand-purple/10',
  },
  {
    icon: Sparkles,
    label: 'Suggest angles',
    prompt: "Based on the brief's persona and insight, suggest 3 more creative angles we haven't tried.",
    color: 'text-violet-500 hover:bg-violet-50',
  },
  {
    icon: Lightbulb,
    label: 'Improve insight',
    prompt: 'Review the Insight section of this brief and suggest a sharper tension statement.',
    color: 'text-amber-500 hover:bg-amber-50',
  },
  {
    icon: GitBranch,
    label: 'PDO grounding',
    prompt: 'Walk me through which buyer scenarios and PDO concepts are driving the creative direction in this brief.',
    color: 'text-teal-500 hover:bg-teal-50',
  },
  {
    icon: Target,
    label: 'Review strategy',
    prompt: "Run a persona-grounded review of this brief's creative strategy. Does the insight, voice, and message form a coherent whole?",
    color: 'text-rose-500 hover:bg-rose-50',
  },
  {
    icon: Zap,
    label: 'Quick wins',
    prompt: 'What are the top 3 highest-impact changes we could make to this brief right now to improve performance potential?',
    color: 'text-orange-500 hover:bg-orange-50',
  },
];

interface ChatSidecarProps {
  brief: GenerationBrief | null;
  chat: UseBriefWorkspaceChatReturn;
  collapsed: boolean;
  activeLens: LensKey;
  onExpand: () => void;
  onCollapse: () => void;
  onActionClick: (prompt: string) => void;
  composerPrefill?: string;
  onPrefillConsumed?: () => void;
  showIntroCallout?: boolean;
  onIntroCalloutDismiss?: () => void;
}

export function ChatSidecar({
  brief,
  chat,
  collapsed,
  activeLens: _activeLens,
  onExpand,
  onCollapse,
  onActionClick,
  composerPrefill,
  onPrefillConsumed,
  showIntroCallout,
  onIntroCalloutDismiss,
}: ChatSidecarProps) {
  return (
    <aside
      className="flex flex-col border-l border-slate-200 bg-white h-full overflow-hidden transition-[width] duration-200 ease-out flex-shrink-0"
      style={{
        width: collapsed
          ? WORKSPACE_LAYOUT.SIDECAR_COLLAPSED_WIDTH
          : WORKSPACE_LAYOUT.SIDECAR_WIDTH,
      }}
    >
      {collapsed ? (
        <CollapsedSidecar onExpand={onExpand} onActionClick={onActionClick} />
      ) : (
        <ExpandedSidecar
          brief={brief}
          chat={chat}
          onCollapse={onCollapse}
          composerPrefill={composerPrefill}
          onPrefillConsumed={onPrefillConsumed}
          showIntroCallout={showIntroCallout}
          onIntroCalloutDismiss={onIntroCalloutDismiss}
        />
      )}
    </aside>
  );
}

function CollapsedSidecar({
  onExpand,
  onActionClick,
}: {
  onExpand: () => void;
  onActionClick: (prompt: string) => void;
}) {
  return (
    <div className="flex flex-col items-center pt-3 gap-1 pb-4">
      {/* Expand button */}
      <button
        onClick={onExpand}
        title="Open chat"
        className="w-8 h-8 rounded-lg bg-brand-purple/10 flex items-center justify-center text-brand-purple hover:bg-brand-purple/20 transition mb-1"
      >
        <ChevronLeft size={15} />
      </button>

      <div className="w-5 border-t border-slate-100 my-1" />

      {/* Quick-action buttons — skip the first (open chat, already above) */}
      {QUICK_ACTIONS.slice(1).map(({ icon: Icon, label, prompt, color }) => (
        <button
          key={label}
          onClick={() => onActionClick(prompt)}
          title={label}
          className={`w-8 h-8 rounded-lg flex items-center justify-center transition ${color}`}
        >
          <Icon size={15} />
        </button>
      ))}
    </div>
  );
}

function ExpandedSidecar({
  brief,
  chat,
  onCollapse,
  composerPrefill,
  onPrefillConsumed,
  showIntroCallout,
  onIntroCalloutDismiss,
}: {
  brief: GenerationBrief | null;
  chat: UseBriefWorkspaceChatReturn;
  onCollapse: () => void;
  composerPrefill?: string;
  onPrefillConsumed?: () => void;
  showIntroCallout?: boolean;
  onIntroCalloutDismiss?: () => void;
}) {
  if (chat.tokenError) {
    return (
      <div className="p-4 text-[11px] text-rose-600 bg-rose-50 border border-rose-200 rounded m-3">
        {chat.tokenError}
      </div>
    );
  }

  if (chat.tokenLoading || !chat.token) {
    return (
      <div className="flex items-center justify-center p-6 text-slate-400 gap-2">
        <Loader2 size={14} className="animate-spin text-brand-purple" />
        <span className="text-[11px]">Opening chat…</span>
      </div>
    );
  }

  return (
    <>
      <SidecarHeader chat={chat} onCollapse={onCollapse} />
      {showIntroCallout && (
        <ChatIntroCallout onDismiss={onIntroCalloutDismiss} />
      )}
      <ContextualSuggestionChips
        brief={brief}
        onChipClick={(prompt) => chat.sendMessage(prompt)}
        disabled={chat.isStreaming}
      />
      <BriefChatPanel
        compact
        messages={chat.messages}
        isStreaming={chat.isStreaming}
        error={chat.error}
        editsRemaining={chat.editsRemaining ?? chat.tokenInfo?.edits_remaining ?? null}
        generationsRemaining={
          chat.generationsRemaining ?? chat.tokenInfo?.generations_remaining ?? null
        }
        activeSections={chat.activeSections}
        activeAngles={chat.activeAngles}
        onSend={chat.sendMessage}
        onUnpin={chat.toggleTarget}
        canChat
        mentionItems={chat.mentionItems}
        prefill={composerPrefill}
        onPrefillConsumed={onPrefillConsumed}
      />
    </>
  );
}

function ChatIntroCallout({ onDismiss }: { onDismiss?: () => void }) {
  return (
    <div className="mx-3 mt-2 mb-1 rounded-lg bg-brand-purple/8 border border-brand-purple/20 p-3 flex gap-2.5 items-start">
      <div className="mt-0.5 flex-shrink-0 w-6 h-6 rounded-md bg-brand-purple/15 flex items-center justify-center">
        <Sparkles size={12} className="text-brand-purple" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[11px] font-semibold text-slate-800 leading-tight">
          Brief Chat is your AI co-pilot
        </p>
        <p className="text-[11px] text-slate-500 mt-0.5 leading-relaxed">
          Ask questions, patch sections, suggest angles, or run a generation — all grounded in the brief's persona data.
        </p>
        <button
          onClick={onDismiss}
          className="mt-2 text-[10px] font-medium text-brand-purple hover:text-brand-purple/80 underline underline-offset-2 transition"
        >
          Got it — minimize chat
        </button>
      </div>
      <button
        onClick={onDismiss}
        title="Dismiss"
        className="flex-shrink-0 w-5 h-5 flex items-center justify-center rounded text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition"
      >
        <X size={11} />
      </button>
    </div>
  );
}

function SidecarHeader({
  chat,
  onCollapse,
}: {
  chat: UseBriefWorkspaceChatReturn;
  onCollapse: () => void;
}) {
  const edits = chat.editsRemaining ?? chat.tokenInfo?.edits_remaining ?? null;
  const generations =
    chat.generationsRemaining ?? chat.tokenInfo?.generations_remaining ?? null;
  return (
    <header className="px-4 py-3 border-b border-slate-200">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-sm font-bold text-slate-900">Brief Chat</h2>
        <div className="flex items-center gap-1.5">
          <div className="flex items-center gap-1 text-[10px]">
            <QuotaPill label="Edits" value={edits} tone="purple" />
            <QuotaPill label="Gen" value={generations} tone="teal" />
          </div>
          <button
            onClick={onCollapse}
            title="Collapse chat"
            className="w-6 h-6 rounded flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition"
          >
            <ChevronRight size={14} />
          </button>
        </div>
      </div>
      <p className="text-[11px] text-slate-500 mt-0.5">
        Ask questions, patch sections, or run a generation.
      </p>
    </header>
  );
}

function QuotaPill({
  label,
  value,
  tone,
}: {
  label: string;
  value: number | null;
  tone: 'purple' | 'teal';
}) {
  if (value == null)
    return (
      <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-400 border border-slate-200">
        {label}: —
      </span>
    );
  const cls =
    value <= 0
      ? 'bg-rose-100 text-rose-700 border-rose-200'
      : value <= 3
        ? 'bg-amber-100 text-amber-700 border-amber-200'
        : tone === 'purple'
          ? 'bg-brand-purple/10 text-indigo-700 border-indigo-200'
          : 'bg-brand-teal/10 text-teal-700 border-teal-200';
  return (
    <span className={`px-1.5 py-0.5 rounded border ${cls}`}>
      {label}: {value}
    </span>
  );
}
